import {
  stepEntity,
  clonePlayer,
  NULL_INPUT,
  type MatchState,
  type PlayerState,
  type PlayerInput,
  type SimConfig,
} from '@fafi/shared';

// Client prediction + reconciliation (§10.2). Predicts the OWN entity via the shared, parity-tested
// `stepEntity`. On an authoritative snapshot at tick T: overwrite the snapshotted fields on the stored
// predicted state at T (keeping client-derived edge state — prevButtons/charge/sprinting), then
// re-apply stored inputs (T, now] to reach the corrected present. Error blends over BLEND_MS; a
// correction > SNAP_LIMIT snaps. Pure — a virtual clock drives it.

/** Authoritative fields the snapshot carries for the own entity (dequantized). */
export interface SnapEntity {
  pos: { x: number; y: number };
  vel: { x: number; y: number };
  facing: number;
  z: number;
  stamina: number;
  action: PlayerState['action'];
  actionTicks: number; // authoritative — pairs with `action` so duration transitions fire at the right tick
}

const BLEND_MS = 100;
const SNAP_LIMIT = 2; // metres

/** Shortest-arc form of an angle difference (facing is periodic). */
function shortestArc(d: number): number {
  let a = d % (Math.PI * 2);
  if (a > Math.PI) a -= Math.PI * 2;
  else if (a < -Math.PI) a += Math.PI * 2;
  return a;
}

export class Predictor {
  private readonly inputs = new Map<number, PlayerInput>();
  private readonly history = new Map<number, PlayerState>(); // predicted own state AT tick t
  private predicted: PlayerState;
  predictedTick: number;
  /** visual correction offset (metres), decayed each advance for smooth reconciliation */
  private corr = { x: 0, y: 0 };

  /** Current reconciliation-correction magnitude in metres (the prediction error being smoothed out). */
  correctionMagnitude(): number {
    return Math.hypot(this.corr.x, this.corr.y);
  }

  constructor(
    private readonly scratch: MatchState, // supplies conditions/mode for stepEntity (players[ownId] is swapped)
    readonly ownId: number,
    startTick: number,
    startState: PlayerState,
  ) {
    this.predicted = clonePlayer(startState);
    this.predictedTick = startTick;
    this.history.set(startTick, clonePlayer(this.predicted));
  }

  recordInput(tick: number, input: PlayerInput): void {
    this.inputs.set(tick, input);
  }

  private stepOne(player: PlayerState, tick: number, cfg: SimConfig): PlayerState {
    this.scratch.players[this.ownId] = player;
    this.scratch.tick = tick;
    return stepEntity(this.scratch, this.ownId, this.inputs.get(tick) ?? NULL_INPUT, cfg);
  }

  /** Advance prediction to `toTick`, applying stored inputs. Decays the visual correction offset. */
  advanceTo(toTick: number, cfg: SimConfig): void {
    // decay the correction offset toward 0 over ~BLEND_MS, PER TICK ADVANCED.
    // D117: this used to decay once per CALL, which was a per-tick decay only by accident — advanceTo
    // was driven by a 30 Hz interval that advanced exactly one tick each time. Driving the pump from the
    // render frame (60–144 Hz) would otherwise collapse the ~100 ms reconciliation blend to ~25 ms and
    // turn every mispredict into a visible pop. Tied to the tick, the blend is refresh-rate-independent.
    const decay = Math.max(0, 1 - (1000 / 30) / BLEND_MS);
    while (this.predictedTick < toTick) {
      this.predicted = this.stepOne(this.predicted, this.predictedTick, cfg);
      this.predictedTick += 1;
      this.history.set(this.predictedTick, clonePlayer(this.predicted));
      this.corr.x *= decay;
      this.corr.y *= decay;
    }
  }

  /** Reconcile against the authoritative own-entity snapshot at `snapTick`. */
  onSnapshot(snapTick: number, snap: SnapEntity, cfg: SimConfig): void {
    if (snapTick > this.predictedTick) return; // future snapshot (shouldn't happen)
    const base = this.history.get(snapTick);
    if (!base) return;
    // authoritative full player at snapTick = client-derived edge state + snapshot's authoritative fields.
    // `action` AND `actionTicks` are both snapshotted (§9.3) — take both, or an unpredicted server action
    // (e.g. a ragdoll the client's stepEntity can't foresee) would inherit the stale idle tick counter and
    // fast-forward through its duration.
    let p: PlayerState = {
      ...base,
      pos: { x: snap.pos.x, y: snap.pos.y },
      vel: { x: snap.vel.x, y: snap.vel.y },
      facing: snap.facing,
      z: snap.z,
      stamina: snap.stamina,
      action: snap.action,
      actionTicks: snap.actionTicks,
    };
    this.history.set(snapTick, clonePlayer(p));
    // remember where we predicted the present before correcting (for the blend offset)
    const beforeX = this.predicted.pos.x;
    const beforeY = this.predicted.pos.y;
    // re-apply inputs (snapTick, predictedTick]
    for (let t = snapTick; t < this.predictedTick; t++) {
      p = this.stepOne(p, t, cfg);
      this.history.set(t + 1, clonePlayer(p));
    }
    this.predicted = p;
    // reconciliation: accumulate this correction onto the still-decaying offset so the rendered position
    // stays continuous across snapshots (overwriting would discard the un-decayed residual → a visual pop).
    // A hard snap (accumulated offset beyond SNAP_LIMIT) zeroes it.
    this.corr.x += beforeX - p.pos.x;
    this.corr.y += beforeY - p.pos.y;
    if (Math.hypot(this.corr.x, this.corr.y) > SNAP_LIMIT) {
      this.corr.x = 0;
      this.corr.y = 0;
    }
    // prune history/inputs older than snapTick
    for (const t of this.history.keys()) if (t < snapTick) this.history.delete(t);
    for (const t of this.inputs.keys()) if (t < snapTick - 1) this.inputs.delete(t);
  }

  /** The authoritative predicted state (no visual offset). */
  state(): PlayerState {
    return this.predicted;
  }

  /** The rendered position = predicted + decaying correction offset (smooth reconciliation). */
  renderedPos(): { x: number; y: number } {
    return this.sample(0).pos;
  }

  /**
   * D117 — the rendered own entity at `alpha` (0..1) of the way through the tick FOLLOWING
   * `predictedTick`. Prediction advances 30×/s; the display refreshes 60–144×/s. Rendering the raw
   * predicted state made the own player — and the follow-camera anchored to him, and so the entire
   * scene — step at the tick rate, out of phase with the display: the "lag" that the zero-latency
   * kickabout (which lerps every entity sub-tick, see toViewState) does not have.
   *
   * Extrapolates FORWARD along the last realised motion rather than interpolating backwards between the
   * last two ticks. Backwards would be perfectly continuous but would put the own player up to a tick in
   * the past, and the whole point of prediction is that your own player is instant.
   *
   * The step comes from the REALISED per-tick displacement (predicted[t] − predicted[t−1]), not from
   * `vel`: an entity held in place by a boundary or a contact still carries velocity, and extrapolating
   * that would shimmer him through the thing stopping him. A still player renders perfectly still.
   */
  sample(alpha: number): { pos: { x: number; y: number }; facing: number } {
    const p = this.predicted;
    const x = p.pos.x + this.corr.x;
    const y = p.pos.y + this.corr.y;
    const a = alpha <= 0 ? 0 : alpha >= 1 ? 1 : alpha;
    const prev = a === 0 ? undefined : this.history.get(this.predictedTick - 1);
    if (!prev) return { pos: { x, y }, facing: p.facing };
    return {
      pos: { x: x + (p.pos.x - prev.pos.x) * a, y: y + (p.pos.y - prev.pos.y) * a },
      facing: p.facing + shortestArc(p.facing - prev.facing) * a,
    };
  }
}
